import { useEffect } from 'react';

/* ---------- Data ---------- */
const userAcquisition = [
  ['TikTok', 'https://ads.tiktok.com/i18n/official/policy/privacy'],
  ['Mintegral', 'https://www.mintegral.com/en/privacy'],
  ['Applovin', 'https://www.applovin.com/privacy/'],
  ['Ironsource', 'https://developers.ironsrc.com/ironsource-mobile/air/ironsource-mobile-privacy-policy/'],
  ['Unity', 'https://unity3d.com/legal/privacy-policy'],
  ['Google', 'https://policies.google.com/privacy?hl=en-US'],
  ['Facebook (Meta)', 'https://www.facebook.com/privacy/policy/'],
  ['NativeEx', 'https://www.nativex.com/en/privacy/'],
  ['Moloco', 'https://www.moloco.com/privacy-policy'],
  ['Liftoff (Vungle)', 'https://vungle.com/privacy/'],
  ['Snapchat', 'https://snap.com/en-US/privacy/privacy-policy'],
  ['Apple', 'https://searchads.apple.com/privacy'],
  ['Inmobi', 'https://www.inmobi.com/privacy-policy'],
];

const monetization = [
  ['Applovin', 'https://www.applovin.com/privacy/'],
  ['BidMachine', 'https://bidmachine.io/privacy-policy/'],
  ['Bigo Ads', 'https://forbusiness.bigo.sg/privacy.html'],
  ['DT Exchange', 'https://www.digitalturbine.com/privacy-policy'],
  ['Admob (Google)', 'https://policies.google.com/privacy?hl=en-US'],
  ['Inmobi', 'https://www.inmobi.com/privacy-policy'],
  ['Ironsource', 'https://developers.ironsrc.com/ironsource-mobile/air/ironsource-mobile-privacy-policy/'],
  ['Liftoff (Vungle)', 'https://vungle.com/privacy/'],
  ['Facebook (Meta)', 'https://www.facebook.com/privacy/policy/'],
  ['Mintegral', 'https://www.mintegral.com/en/privacy'],
  ['Mobilefuse', 'https://mobilefuse.com/privacy-policy'],
  ['Moloco', 'https://www.moloco.com/privacy-policy'],
  ['Ogury', 'https://ogury.com/ogury-advertising-privacy-policy/'],
  ['Pubmatic', 'https://pubmatic.com/legal/privacy-policy/'],
  ['Pangle', 'https://www.pangleglobal.com/privacy/enduser-en'],
  ['Smaato', 'https://www.smaato.com/privacy/'],
  ['Unity', 'https://unity3d.com/legal/privacy-policy'],
  ['Yandex', 'https://yandex.com/legal/confidential/08122017/'],
  ['YSO Network', 'https://www.ysonetwork.com/privacy'],
  ['Chartboost', 'https://www.chartboost.com/legal/privacy-policy/'],
  ['Google Ad Manager', 'https://support.google.com/admanager/answer/10075997?hl=en'],
  ['HyprMX', 'https://www.hyprmx.com/privacy-policy.html'],
  ['Reklamup', 'https://reklamup.com/privacy.php'],
  ['A4G', 'https://a4g.com/privacy'],
  ['Premium Ads', 'https://premiumads.net/privacy/'],
  ['Gadsme', 'https://www.gadsme.com/privacy-policy'],
  ['Adverty', 'https://adverty.com/privacy-policy-2021'],
  ['Audiomob', 'https://audiomob.com/privacy-policy'],
  ['TopOn', 'https://www.toponad.com/en/privacy-policy'],
  ['Playgap', 'https://playgap.io/policies/privacy'],
];

const analytics = [
  ['GameAnalytics', 'https://gameanalytics.com/trust/privacy/privacy-notice'],
  ['AppMetrica', 'https://appmetrica.yandex.com/docs/en/data-security/gdpr'],
  ['Adjust', 'https://www.adjust.com/terms/privacy-policy'],
  ['Bytebrew', 'https://docs.bytebrew.io/BBSettings/privacypolicy'],
  ['Firebase', 'https://firebase.google.com/support/privacy'],
  ['Google Analytics', 'https://support.google.com/analytics/answer/9019185?hl=en'],
];

const social = [
  ['Facebook SDK', 'https://www.facebook.com/privacy/policy/'],
  ['Google Play Games SDK', 'https://play.google/developer-content-policy/'],
  ['Apple Game Center SDK', 'https://www.apple.com/legal/privacy/data/en/game-center/'],
];

/* ---------- Small building blocks ---------- */
const Section = ({ title, children }) => (
  <section className="bg-white/80 rounded-2xl border border-gray-100 p-5 sm:p-7 mb-6 shadow-lg shadow-red-100/40 hover:border-red-300 hover:shadow-xl hover:shadow-red-100/60 transition-all duration-300">
    <h2 className="relative font-poppins text-xl sm:text-2xl font-bold text-[#ec2028] pb-3 mb-4 after:content-[''] after:absolute after:left-0 after:bottom-0 after:w-12 after:h-[3px] after:bg-[#ec2028] after:rounded">
      {title}
    </h2>
    {children}
  </section>
);

const H3 = ({ children }) => (
  <h3 className="font-poppins text-lg font-semibold text-gray-900 mt-5 mb-2">{children}</h3>
);

const P = ({ children }) => <p className="text-gray-600 leading-relaxed mb-3">{children}</p>;

const UL = ({ items }) => (
  <ul className="list-disc pl-6 mb-4 text-gray-600 marker:text-[#ec2028] space-y-2">
    {items.map((t, i) => (
      <li key={i} className="leading-relaxed">{t}</li>
    ))}
  </ul>
);

const Note = ({ label, children }) => (
  <div className="bg-red-50 border-l-4 border-[#ec2028] rounded-r-xl px-5 py-4 my-4 text-gray-600 italic">
    <strong className="text-[#ec2028] not-italic">{label}</strong> {children}
  </div>
);

const LinkList = ({ items }) => (
  <ul className="list-disc pl-6 mb-4 marker:text-[#ec2028] sm:columns-2 gap-10">
    {items.map(([name, url]) => (
      <li key={name} className="mb-1.5 break-inside-avoid">
        <a href={url} target="_blank" rel="noopener noreferrer"
           className="text-gray-700 hover:text-[#ec2028] hover:underline transition-colors">
          {name}
        </a>
      </li>
    ))}
  </ul>
);

/* ---------- Page ---------- */
const PrivacyPolicy = () => {
  useEffect(() => {
    document.title = 'Privacy Policy | UB Creations';
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="font-roboto bg-white">
      {/* Header */}
      <header className="relative overflow-hidden bg-gradient-to-br from-white via-red-50 to-white pt-28 pb-16 px-4 text-center">
        <div className="absolute -top-24 -left-24 w-96 h-96 bg-red-200/40 rounded-full blur-3xl pointer-events-none" />
        <div className="absolute -bottom-32 -right-24 w-96 h-96 bg-rose-200/40 rounded-full blur-3xl pointer-events-none" />
        <div className="relative z-10 max-w-3xl mx-auto">
          <h1 className="font-poppins text-4xl md:text-5xl font-bold">
            <span className="bg-gradient-to-r from-red-600 via-red-500 to-rose-500 bg-clip-text text-transparent">
              Privacy Policy
            </span>
          </h1>
          <p className="mt-4 text-gray-600">
            Welcome to UB Creations. We are dedicated to protecting your personal data and ensuring
            transparency in how we handle it.
          </p>
          <p className="mt-2 text-sm text-gray-400">Last Updated: September 28, 2026</p>
        </div>
      </header>

      {/* Content */}
      <main className="max-w-4xl mx-auto px-4 sm:px-6 py-12">
        <Section title="1. Introduction">
          <P>Welcome to UB Creations ("we," "our," or "us"). We are dedicated to protecting your personal data and ensuring transparency in how we handle it.</P>
          <P>This Privacy Policy explains how we collect, use, store, and share your data when you play our mobile games ("Games" or "Services"). By continuing to use our Games, you confirm your acceptance of this policy.</P>
          <P>This policy complies with:</P>
          <UL items={[
            'General Data Protection Regulation (GDPR) – For users in the European Economic Area (EEA) and the UK',
            'California Consumer Privacy Act (CCPA) – For California residents',
          ]} />
        </Section>

        <Section title="2. Information We Collect">
          <P>We collect information to operate, improve, and secure our Games. The data we collect falls into three categories:</P>

          <H3>2.1 Personal Data (Collected When Provided by You)</H3>
          <UL items={[
            'Name and contact information (if provided during account creation or support inquiries)',
            'Payment information (processed securely via payment providers, e.g., Google Play, Apple Pay — we do not store payment details)',
            'User-generated content (such as text, chat messages, or profile data, if applicable)',
          ]} />
          <Note label="Opt-Out:">Users can choose not to provide personal data, but some features may not work.</Note>

          <H3>2.2 Non-Personal Data (Collected Automatically)</H3>
          <UL items={[
            'Device information: We also receive information from third parties to allow us to track performance of our marketing campaigns and to detect fraud in such campaigns. This includes advertising ID, location, transaction information, Model, OS, and IP address (stored temporarily for security purposes)',
            'Usage and gameplay data: Session times, in-game actions, interactions with features.',
          ]} />

          <H3>2.3 Third-Party Data Sources</H3>
          <UL items={[
            'Social logins: If you log in to a game with your social media account, we may receive information about you including your name, profile picture and email address. We may also receive information from third parties such as social media platforms',
            'Ad and analytics providers: We receive data on ad interactions and game performance',
          ]} />

          <H3>2.4 Device Permissions</H3>
          <P>We may request access to features like camera, microphone and storage for certain in game functionalities.</P>
          <Note label="Opt-Out:">Users can choose not to provide permissions, but some features may not work.</Note>
          <P><strong>User Control:</strong> You can disable permissions via:</P>
          <ul className="list-disc pl-6 mb-4 text-gray-600 marker:text-[#ec2028] space-y-2">
            <li><strong>Android:</strong> Settings → Apps → [Game] → Permissions</li>
            <li><strong>iOS:</strong> Settings → Privacy → [Permission Type] → [Game]</li>
          </ul>
          <P>If permissions are denied, certain game features may not function properly.</P>
        </Section>

        <Section title="3. How We Use Your Information">
          <P>For a better experience, we may use your data to:</P>
          <UL items={['Improve the game experience', 'Deliver relevant advertisements']} />
          <Note label="Consent Notice:">In some regions (e.g., the EU), we will ask for your explicit consent before collecting data for advertising.</Note>
        </Section>

        <Section title="4. Third-Party Services & SDKs We Use">
          <P>We integrate third-party services and software development kits (SDKs) for analytics, advertising, and monetization. These third parties may collect user data under their privacy policies.</P>

          <H3>4.1 Advertising and Monetization SDKs</H3>
          <P><strong>User Acquisition</strong></P>
          <LinkList items={userAcquisition} />
          <P><strong>Monetization</strong></P>
          <LinkList items={monetization} />

          <H3>4.2 Analytics and Performance Monitoring</H3>
          <LinkList items={analytics} />

          <H3>4.3 Social Media and Authentication SDKs</H3>
          <LinkList items={social} />

          <Note label="Disclaimer:">Third-party services have their own privacy policies, which we do not control.</Note>
        </Section>

        <Section title="5. Data Sharing and Disclosure">
          <P>We do not sell your personal data. However, we may disclose data in the following situations:</P>
          <UL items={[
            'To comply with legal obligations or law enforcement requests',
            'To protect our rights, prevent fraud, and enhance security',
            'As part of a business transfer, merger, or acquisition',
          ]} />
          <P>We may share aggregated and anonymized data for analytics and industry research.</P>
        </Section>

        <Section title="6. Data Retention and Deletion">
          <P>We retain personal data (such as device information, gameplay and analytics data) only as long as necessary for the purposes stated in this policy.</P>
          <P>We delete data automatically after a certain period of time.</P>
        </Section>

        <Section title="7. Changes to This Privacy Policy">
          <P>We may update this Privacy Policy periodically. Any changes will be posted on this page with an updated "Last Updated" date.</P>
          <P>If significant changes occur, we may notify users through:</P>
          <UL items={['In-game notifications', 'Email (if applicable)']} />
          <P>Users should review this Privacy Policy regularly.</P>
        </Section>

        <Section title="8. Children">
          <P>Our services are not intended for children under 13. We do not knowingly collect personal data from anyone under this age. If we discover that a child under 13 has provided personal information, you may contact us.</P>
          <P>If you are a parent or guardian and believe your child has shared personal information with us, please contact us so we can take the necessary steps.</P>
        </Section>

        <Section title="9. Security">
          <P>We prioritize protecting your personal information and use commercially accepted security measures. However, no method of internet transmission or electronic storage is 100% secure, and we cannot guarantee absolute security.</P>
        </Section>

        <Section title="10. Push Notifications">
          <P>We may send push notifications to your device for game updates, promotions, and other relevant messages — only if you have agreed to receive them.</P>
          <P>You can turn off push notifications anytime by adjusting your device's notification settings.</P>
        </Section>

        <Section title="11. Contact Us">
          <P>For any questions or concerns about this Privacy Policy, please contact:</P>
          <div className="bg-red-50 border-l-4 border-[#ec2028] rounded-r-xl px-5 py-4 text-gray-600 space-y-1">
            <p className="font-semibold text-gray-900">UB Creations Pvt Ltd – Privacy Team</p>
            <p>
              Email:{' '}
              <a href="mailto:ubcreationspvtltd@gmail.com" className="text-[#ec2028] font-semibold hover:underline">
                ubcreationspvtltd@gmail.com
              </a>
            </p>
            <p>Address: Plot Number 2, Riaz ul jannah, Bahawalpur, Pakistan, 63100</p>
          </div>
        </Section>
      </main>
    </div>
  );
};

export default PrivacyPolicy;